import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser } from '@personalizehq/types';
import { supabase } from '@/lib/supabase';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signOut: () => Promise<void>;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,

      initialize: async () => {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();

          if (session?.user) {
            const authUser: AuthUser = {
              id: session.user.id,
              email: session.user.email ?? '',
              name: session.user.user_metadata?.name,
              avatarUrl: session.user.user_metadata?.avatar_url,
              provider: (session.user.app_metadata.provider as
                | 'email'
                | 'google'
                | 'github') ?? 'email',
              emailVerified: session.user.email_confirmed_at !== null,
              createdAt: session.user.created_at,
              updatedAt: session.user.updated_at ?? session.user.created_at,
            };

            set({
              user: authUser,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            set({ user: null, isAuthenticated: false, isLoading: false });
          }
        } catch (error) {
          console.error('Error initializing auth:', error);
          set({ user: null, isAuthenticated: false, isLoading: false });
        }
      },

      signIn: async (email: string, password: string) => {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          throw error;
        }

        if (data.user) {
          const authUser: AuthUser = {
            id: data.user.id,
            email: data.user.email ?? '',
            // OPTIONAL. `user_metadata` is absent for a user who signed up without a profile, and an
            // unguarded read here threw INSIDE auth initialisation — a hard crash on boot, not a
            // missing display name.
            name: data.user.user_metadata?.name,
            avatarUrl: data.user.user_metadata?.avatar_url,
            provider: 'email',
            emailVerified: data.user.email_confirmed_at !== null,
            createdAt: data.user.created_at,
            updatedAt: data.user.updated_at ?? data.user.created_at,
          };

          set({ user: authUser, isAuthenticated: true });
        }
      },

      signUp: async (email: string, password: string, name?: string) => {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name,
            },
          },
        });

        if (error) {
          throw error;
        }

        if (data.user) {
          const authUser: AuthUser = {
            id: data.user.id,
            email: data.user.email ?? '',
            // OPTIONAL. `user_metadata` is absent for a user who signed up without a profile, and an
            // unguarded read here threw INSIDE auth initialisation — a hard crash on boot, not a
            // missing display name.
            name: data.user.user_metadata?.name,
            avatarUrl: data.user.user_metadata?.avatar_url,
            provider: 'email',
            emailVerified: data.user.email_confirmed_at !== null,
            createdAt: data.user.created_at,
            updatedAt: data.user.updated_at ?? data.user.created_at,
          };

          set({ user: authUser, isAuthenticated: true });
        }
      },

      signOut: async () => {
        await supabase.auth.signOut();
        set({ user: null, isAuthenticated: false });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Initialize auth on app start
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_IN' && session?.user) {
    const authUser: AuthUser = {
      id: session.user.id,
      email: session.user.email ?? '',
      name: session.user.user_metadata?.name,
      avatarUrl: session.user.user_metadata?.avatar_url,
      provider: (session.user.app_metadata.provider as
        | 'email'
        | 'google'
        | 'github') ?? 'email',
      emailVerified: session.user.email_confirmed_at !== null,
      createdAt: session.user.created_at,
      updatedAt: session.user.updated_at ?? session.user.created_at,
    };

    useAuthStore.setState({
      user: authUser,
      isAuthenticated: true,
    });
  } else if (event === 'SIGNED_OUT') {
    useAuthStore.setState({
      user: null,
      isAuthenticated: false,
    });
  }
});
